import React, {
  useLayoutEffect,
  useEffect,
  useState,
  useRef,
} from 'react';
import dayjs from 'dayjs';
import LoadPanel from 'devextreme-react/load-panel';
import { Popup } from 'devextreme-react/popup';
import Button from 'devextreme-react/button';
import TextArea from 'devextreme-react/text-area';
import {
  Validator,
  RequiredRule
} from 'devextreme-react/validator';
import { useNavigate } from "react-router-dom";
import { useGlobalState } from '../../contexts/global-state';
import { usePageTitle } from '../../contexts/page-title';
import {
  useAuthService,
  useStaffListApiService,
} from "../../services";
import { showError, showSuccess } from '../../helpers/ui';
import { useScreenSize } from '../../utils/media-query';
import '../../components/component.scss';
// Default import: a namespace import (`* as`) only exposes class names that are
// valid JS identifiers, so 'card-header' / 'card-body' came back undefined.
import cardStyles from '../../components/card-container/card/card.module.scss';
import styles from './home.module.scss';

// Card stats for one school's staff list. Staff whose profile was last reviewed
// more than a year ago, or never, count as outdated.
function summarizeStaffList(staffList) {
  const oneYearAgo = dayjs().subtract(1, 'year');
  const members = new Map((staffList || []).map(s => [s.memberId, s]));
  let missingEmail = 0;
  let outdatedReview = 0;
  members.forEach(s => {
    if (!s.hasHomeEmailAddress) missingEmail++;
    if (!s.recordReviewedByMember || dayjs(new Date(s.recordReviewedByMember)).isBefore(oneYearAgo)) outdatedReview++;
  });
  return { totalStaff: members.size, missingEmail, outdatedReview };
}

export default function () {
  const { managedSchools, setManagedSchools, committees } = useGlobalState();
  const navigate = useNavigate();
  const user = useAuthService().getActiveAccount();
  const { idTokenClaims: { givenName, memberId } } = user || { idTokenClaims: {} }
  const { setTitle } = usePageTitle();
  const [showChangePopup, setShowChangePopup] = useState(false);
  const [selectedCSRRoleName, setSelectedCSRRoleName] = useState('');
  const [selectedSchoolName, setSelectedSchoolName] = useState('');
  const [selectedMemberCsrRoleId, setSelectedMemberCsrRoleId] = useState(0);
  const [showLoadPanel, setShowLoadPanel] = useState(false);

  const textAreaRef = useRef(null);

  const { isSmall, isXSmall } = useScreenSize();
  const { sendEmailToChangeCSRRole, getSchoolsForWhichUserIsAStaffListManagerAsync, getStaffListBySchoolAsync, getCommitteeSummaryAsync } = useStaffListApiService();
  // { [schoolId]: { totalStaff, missingEmail, outdatedReview } | { error: true } }
  const [staffStats, setStaffStats] = useState({});
  const managedSchoolIdsKey = [...new Set(managedSchools.map(s => s.schoolId))].join(',');
  // { [committeeId]: { dateJoined, memberCount, documentCount } | { error: true } }
  const [committeeStats, setCommitteeStats] = useState({});
  const committeeIdsKey = (committees || []).map(c => c.committeeId).join(',');

  useLayoutEffect(() => setTitle("Epitome Member Access"), [setTitle]);

  useEffect(() => {
    if (managedSchools.length === 0) {
      getSchoolsForWhichUserIsAStaffListManagerAsync()
        .then(r => { if (r.data) setManagedSchools(r.data); })
        .catch(e => showError(e.message));
    }
  }, [managedSchools.length, getSchoolsForWhichUserIsAStaffListManagerAsync, setManagedSchools]);

  // Load each managed school's staff list in parallel for the card stats.
  useEffect(() => {
    if (!managedSchoolIdsKey) return;
    let cancelled = false;
    managedSchoolIdsKey.split(',').forEach(schoolId => {
      getStaffListBySchoolAsync(schoolId)
        .then(r => !cancelled && setStaffStats(prev => ({ ...prev, [schoolId]: summarizeStaffList(r.data) })))
        .catch(() => !cancelled && setStaffStats(prev => ({ ...prev, [schoolId]: { error: true } })));
    });
    return () => { cancelled = true; };
  }, [managedSchoolIdsKey, getStaffListBySchoolAsync]);

  // Load each committee's summary (date joined, members, documents) in parallel.
  useEffect(() => {
    if (!committeeIdsKey) return;
    let cancelled = false;
    committeeIdsKey.split(',').forEach(committeeId => {
      getCommitteeSummaryAsync(committeeId)
        .then(r => !cancelled && setCommitteeStats(prev => ({ ...prev, [committeeId]: r.data })))
        .catch(() => !cancelled && setCommitteeStats(prev => ({ ...prev, [committeeId]: { error: true } })));
    });
    return () => { cancelled = true; };
  }, [committeeIdsKey, getCommitteeSummaryAsync]);

  function formatDateJoined(value) {
    return dayjs(new Date(value)).format('MMM D, YYYY');
  }

  // '…' while loading; '—' on error or when a value isn't available (e.g. the
  // document count when blob storage can't be reached).
  function renderStat(stats, field, format = v => v) {
    if (!stats) return '…';
    const value = stats[field];
    if (stats.error || value === null || value === undefined) return '—';
    return format(value);
  }
  function renderChangePopup() {
    return (
      <Popup
        visible={showChangePopup}
        title='Change Role'
        width={(isSmall || isXSmall) ? "100%" : "50%"}
        height={(isSmall || isXSmall) ? "100%" : "50%"}
        showCloseButton={true}
        onHiding={() => {
          setShowChangePopup(false);
        }}
      >
        <p>Please let us know the approximate date you stopped being the {selectedCSRRoleName} in the school {selectedSchoolName}.  If somebody has replaced you please include their name in your message, and if you have taken on another role please indicate which role.</p>
        <TextArea
          autoResizeEnabled={false}
          height="50%"
          ref={textAreaRef}
        >
          <Validator>
            <RequiredRule />
          </Validator>
        </TextArea>
        <div className={styles['send-email-popup-buttons']}>
          <Button
            className={styles['send-button']}
            width='30%'
            text='Send'
            onClick={async (e) => {
              textAreaRef.current.instance().validationRequest.fire();
              if (!textAreaRef.current.instance().option('isValid')) return;
              try {
                e.component.option('disabled', true);
                setShowLoadPanel(true);
                await sendEmailToChangeCSRRole(textAreaRef.current.instance().option('value'), selectedMemberCsrRoleId);
                setShowChangePopup(false);
                setShowLoadPanel(false);
                e.component.option('disabled', false);
                showSuccess('Email has been sent.')
              } catch (error) {
                e.component.option('disabled', false);
                setShowLoadPanel(false);
                showError(error.message);
              }
            }}
          />
          <Button
            className={styles['cancel-button']}
            width='30%'
            text='Cancel'
            onClick={() => {
              setShowChangePopup(false);
            }}
          />
        </div>
      </Popup>
    )
  }

  const groupedSchools = [...new Set(managedSchools.map(item => item.schoolId))];
  return (user ? (
    <div className="responsive-paddings">
      <LoadPanel
        visible={showLoadPanel}
      />
      <div className="title">
        <p id="welcomeMessage">Welcome To Epitome Member Access, {givenName}</p>
      </div>
      <div>
        <Button
          text="My Member Profile" id="myProfile"
          onClick={
            async () => {
              navigate(`/edit-profile/${memberId}`);
            }
          }
        />
      </div>
      {(managedSchools && managedSchools.length > 0) && <h1>Staff Lists You Manage</h1>}
      <div className={styles["schools-container"]}>
        {groupedSchools && groupedSchools.map((schoolId) => {
          return (
            <div className={cardStyles['card']} key={schoolId}>
              <div className={cardStyles['card-header']}>
                <h2 className={cardStyles['card-title']}>{managedSchools.find(x => x.schoolId === schoolId).name}</h2>
                {/* The user's role(s) at this school, each with a link to request a change. */}
                {managedSchools.filter(x => x.schoolId === schoolId).map((x) => (
                  <div className={styles['role-subheading']} key={x.id}>
                    <p className={cardStyles['card-subtitle']}>{x.description}</p>
                    <button
                      type="button"
                      className={styles['change-role-link']}
                      onClick={() => {
                        setShowChangePopup(true);
                        setSelectedCSRRoleName(x.description);
                        setSelectedMemberCsrRoleId(x.id);
                        setSelectedSchoolName(managedSchools.find(s => s.schoolId === schoolId).name);
                      }}
                    >
                      Change…
                    </button>
                  </div>
                ))}
              </div>
              <div className={cardStyles['card-body']}>
                <dl className={cardStyles['card-stats']}>
                  <dt>Total Staff</dt>
                  <dd>{renderStat(staffStats[schoolId], 'totalStaff')}</dd>
                  <dt>Missing Email Addresses</dt>
                  <dd>{renderStat(staffStats[schoolId], 'missingEmail')}</dd>
                  <dt title="Profile last reviewed more than a year ago, or never">Outdated Profile Reviews</dt>
                  <dd>{renderStat(staffStats[schoolId], 'outdatedReview')}</dd>
                </dl>
              </div>
              <div className={styles['manage-button']}>
                <Button
                  text="Manage Staff List"
                  width="80%"
                  tag={schoolId}
                  onClick={(e) => {
                    navigate(`/school/${e.component.option('tag')}`);
                  }}
                />
              </div>
            </div>
          );
        })
        }
      </div>



      {(committees && committees.length > 0) && <h1>Committees You Are Member Of</h1>}
      <div className={styles["schools-container"]}>
        {committees && committees.map((committee) => {
          return (
            <div className={cardStyles['card']} key={committee.committeeId}>
              <div className={cardStyles['card-header']}>
                <h2 className={cardStyles['card-title']}>{committee.committeeName}</h2>
              </div>
              <div className={cardStyles['card-body']}>
                <dl className={cardStyles['card-stats']}>
                  <dt>Date Joined</dt>
                  <dd>{renderStat(committeeStats[committee.committeeId], 'dateJoined', formatDateJoined)}</dd>
                  <dt>Members</dt>
                  <dd>{renderStat(committeeStats[committee.committeeId], 'memberCount')}</dd>
                  <dt>Documents</dt>
                  <dd>{renderStat(committeeStats[committee.committeeId], 'documentCount')}</dd>
                </dl>
              </div>
              <div className={styles['manage-button']}>
                <Button
                  text="View Documents"
                  icon="folder"
                  width="80%"
                  tag={committee.committeeId}
                  onClick={(e) => {
                    navigate(`/committee-documents-grid/${e.component.option('tag')}`);
                  }}
                />
              </div>
            </div>
          );
        })
        }
      </div>
      {showChangePopup && renderChangePopup()}
    </div>
  ) : <LoadPanel />);
}
